import { useState, useCallback } from 'react'
import { devLog } from '../services/devLog'

const PLACEHOLDER_REGEX = /\{\{([a-z0-9_]+)\}\}/gi

/**
 * Sanitize a variable name: lowercase, underscores only, strip invalid chars.
 */
export function sanitizeVariableName(input: string): string {
  return input
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/^_+|_+$/g, '')
}

/**
 * Office.js hook for inserting and scanning {{variable}} placeholders in Word.
 * Follows the same pattern as useSignatureTags.ts.
 */
export function useTemplatePlaceholders() {
  const [isInserting, setIsInserting] = useState(false)
  const [isScanning, setIsScanning] = useState(false)

  /** Insert {{name}} at the current cursor position in Word */
  const insertPlaceholder = useCallback(async (name: string): Promise<void> => {
    const sanitized = sanitizeVariableName(name)
    if (!sanitized) return

    setIsInserting(true)
    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection()
        selection.insertText(`{{${sanitized}}}`, Word.InsertLocation.replace)
        await context.sync()
      })
      devLog.info('Placeholder inserted', `{{${sanitized}}}`)
    } catch (err) {
      devLog.error('Insert placeholder failed', String(err))
      throw err
    } finally {
      setIsInserting(false)
    }
  }, [])

  /** Scan the document body for all {{...}} placeholders. Returns unique sorted names. */
  const scanPlaceholders = useCallback(async (): Promise<string[]> => {
    setIsScanning(true)
    try {
      const names = await Word.run(async (context) => {
        const body = context.document.body
        body.load('text')
        await context.sync()

        const found = new Set<string>()
        let match: RegExpExecArray | null

        PLACEHOLDER_REGEX.lastIndex = 0
        while ((match = PLACEHOLDER_REGEX.exec(body.text)) !== null) {
          found.add(match[1].toLowerCase())
        }

        return [...found].sort()
      })

      devLog.info('Placeholder scan', `Found ${names.length}: ${names.join(', ')}`)
      return names
    } catch (err) {
      devLog.error('Placeholder scan failed', String(err))
      return []
    } finally {
      setIsScanning(false)
    }
  }, [])

  return { insertPlaceholder, scanPlaceholders, isInserting, isScanning }
}
